import React from 'react';
import { TemplateProps } from '../../../types/cv';
import { Mail, Phone, MapPin, Globe } from 'lucide-react';
import { CV_LABELS, frPresent } from '../../../utils/cvLabels';
import { isSectionContinuation } from '../../../utils/cvPagination';
import SectionHeading from '../SectionHeading';

/**
 * Earth Tones — en-tête / pied de page colorés = décor uniquement.
 * Seule la photo chevauche l'en-tête ; tout le texte reste dans la zone blanche.
 */
const EarthTonesTemplate: React.FC<TemplateProps> = ({
    data,
    isContinuationPage = false,
    showPlaceholders = false,
    pageIndex = 0,
    fullData,
}) => {
    const source = fullData || data;
    const eduContinued = isSectionContinuation(source, 'education', pageIndex);
    const expContinued = isSectionContinuation(source, 'experience', pageIndex);
    const langContinued = isSectionContinuation(source, 'languages', pageIndex);
    const skillsContinued = isSectionContinuation(source, 'skills', pageIndex);
    const primaryColor = data.style?.primaryColor || '#B8860B';
    const {
        fontFamily = 'font-sans',
        textColor = '#334155',
        backgroundColor = '#ffffff',
        imageBorderColor = primaryColor,
    } = data.style || {};

    const phone = data.personalInfo.phone || (showPlaceholders ? '+1 123-456-7890' : '');
    const email = data.personalInfo.email || (showPlaceholders ? 'annewinston@email.com' : '');
    const address = data.personalInfo.address || (showPlaceholders ? 'Abidjan, Côte d\'Ivoire' : '');
    const website = data.personalInfo.website || (showPlaceholders ? 'www.exemple.com' : '');
    const summary =
        data.personalInfo.summary ||
        (showPlaceholders
            ? 'Professionnel motivé avec plusieurs années d\'expérience, orienté résultats et travail d\'équipe.'
            : '');
    const photoSrc =
        data.personalInfo.profileImage ||
        (showPlaceholders
            ? 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=1974&auto=format&fit=crop'
            : '');

    const hasSkills = (data.skills && data.skills.length > 0) || showPlaceholders;
    const hasEducation = data.education.length > 0 || showPlaceholders;
    const hasExperience = data.experience.length > 0 || showPlaceholders;
    const hasLanguages = (data.languages && data.languages.length > 0) || showPlaceholders;

    return (
        <div
            className={`shadow-2xl cv-page-sheet h-[297mm] max-h-[297mm] w-full relative overflow-hidden ${fontFamily}`}
            style={{ backgroundColor, color: textColor }}
        >
            {/* En-tête décoratif — aucun texte dedans */}
            <div
                className="absolute top-0 left-0 w-full h-28 pointer-events-none z-0"
                style={{ backgroundColor: primaryColor }}
                aria-hidden
            >
                <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="absolute bottom-0 w-full h-14">
                    <path d="M0,40 C300,100 600,0 900,50 L1200,40 L1200,120 L0,120 Z" fill={backgroundColor || '#ffffff'} />
                </svg>
            </div>

            {/* Pied de page décoratif — aucun texte dedans */}
            <div
                className="absolute bottom-0 left-0 w-full h-24 pointer-events-none z-0"
                style={{ backgroundColor: primaryColor }}
                aria-hidden
            >
                <svg viewBox="0 0 1200 100" preserveAspectRatio="none" className="absolute top-0 w-full h-12">
                    <path d="M0,60 C300,0 600,100 900,40 L1200,60 L1200,0 L0,0 Z" fill={backgroundColor || '#ffffff'} />
                </svg>
            </div>

            {/* Contenu dans la zone blanche (marges hors bandeaux) */}
            <div className="relative z-10 flex h-[297mm] max-h-[297mm] px-8 pb-28">
                {/* Colonne gauche */}
                <div data-cv-sidebar className="w-[40%] pr-6 flex flex-col overflow-hidden">
                    {isContinuationPage ? (
                        <div className="pt-32">
                            <p className="cv-fs-name text-xs font-bold uppercase tracking-widest" style={{ color: textColor, opacity: 0.7 }}>
                                {data.personalInfo.firstName} {data.personalInfo.lastName}
                            </p>
                            <p className="text-[10px] uppercase tracking-wider mt-2 mb-4" style={{ color: textColor, opacity: 0.4 }}>
                                {CV_LABELS.suite}
                            </p>
                            {data.skills && data.skills.length > 0 && (
                                <section className="mb-4">
                                    <SectionHeading
                                        title={CV_LABELS.skills}
                                        isContinuation={skillsContinued}
                                        primaryColor={primaryColor}
                                        variant="plain"
                                    />
                                    <div className="cv-fs-skills space-y-2">
                                        {data.skills.map((skill) => (
                                            <div
                                                key={skill.id || skill.name}
                                                data-cv-block
                                                data-cv-item-id={skill.id || undefined}
                                            >
                                                <div className="flex justify-between mb-0.5">
                                                    <span className="text-xs font-bold">{skill.name}</span>
                                                </div>
                                                <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full rounded-full"
                                                        style={{ width: `${skill.level}%`, backgroundColor: primaryColor }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}
                        </div>
                    ) : (
                        <>
                            {/* Photo seule qui chevauche l'en-tête coloré */}
                            {(photoSrc || showPlaceholders) && (
                                <div className="mt-6 mb-4 relative z-20">
                                    <div
                                        className="w-36 h-36 rounded-full border-[8px] overflow-hidden shadow-xl bg-slate-200"
                                        style={{ borderColor: imageBorderColor }}
                                    >
                                        {photoSrc ? (
                                            <img src={photoSrc} alt="Profil" className="w-full h-full object-cover" />
                                        ) : null}
                                    </div>
                                </div>
                            )}

                            {/* Tout le reste strictement sous l'en-tête, dans le blanc */}
                            <div className={photoSrc || showPlaceholders ? 'pt-1' : 'pt-32'}>
                                <div className="mb-4 w-full break-words">
                                    <h1 className="cv-fs-name text-3xl font-black uppercase leading-tight mb-1">
                                        {data.personalInfo.firstName || (showPlaceholders ? 'PRÉNOM' : '')}
                                        <br />
                                        {data.personalInfo.lastName || (showPlaceholders ? 'NOM' : '')}
                                    </h1>
                                    <p className="cv-fs-profession text-base font-bold" style={{ color: primaryColor }}>
                                        {data.personalInfo.profession || (showPlaceholders ? 'Votre métier' : '')}
                                    </p>
                                </div>

                                {summary && (
                                    <p className="cv-fs-summary text-xs leading-relaxed mb-4 break-words" style={{ color: textColor }}>
                                        {summary}
                                    </p>
                                )}

                                {(phone || email || address || website || showPlaceholders) && (
                                    <section className="mb-5">
                                        <h2 className="text-lg font-black mb-3 uppercase" style={{ color: textColor }}>
                                            {CV_LABELS.contact}
                                        </h2>
                                        <div className="cv-fs-contact space-y-2.5">
                                            {email && (
                                                <div className="flex items-center gap-2.5">
                                                    <div
                                                        className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                                                        style={{ backgroundColor: primaryColor }}
                                                    >
                                                        <Mail className="w-3.5 h-3.5 text-white" />
                                                    </div>
                                                    <span className="text-xs break-all">{email}</span>
                                                </div>
                                            )}
                                            {phone && (
                                                <div className="flex items-center gap-2.5">
                                                    <div
                                                        className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                                                        style={{ backgroundColor: primaryColor }}
                                                    >
                                                        <Phone className="w-3.5 h-3.5 text-white" />
                                                    </div>
                                                    <span className="text-xs">{phone}</span>
                                                </div>
                                            )}
                                            {address && (
                                                <div className="flex items-center gap-2.5">
                                                    <div
                                                        className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                                                        style={{ backgroundColor: primaryColor }}
                                                    >
                                                        <MapPin className="w-3.5 h-3.5 text-white" />
                                                    </div>
                                                    <span className="text-xs break-words">{address}</span>
                                                </div>
                                            )}
                                            {website && (
                                                <div className="flex items-center gap-2.5">
                                                    <div
                                                        className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                                                        style={{ backgroundColor: primaryColor }}
                                                    >
                                                        <Globe className="w-3.5 h-3.5 text-white" />
                                                    </div>
                                                    <span className="text-xs break-all">{website}</span>
                                                </div>
                                            )}
                                        </div>
                                    </section>
                                )}

                                {hasSkills && (
                                    <section className="mb-4">
                                        <SectionHeading
                                            title={CV_LABELS.skills}
                                            isContinuation={skillsContinued}
                                            primaryColor={textColor}
                                            variant="plain"
                                            textClassName="text-lg"
                                        />
                                        <div className="cv-fs-skills space-y-2">
                                            {(data.skills && data.skills.length > 0
                                                ? data.skills
                                                : [
                                                      { name: 'Leadership', level: 85 },
                                                      { name: 'Organisation', level: 80 },
                                                      { name: 'Créativité', level: 90 },
                                                  ]
                                            ).map((skill) => (
                                                <div
                                                    key={skill.id || skill.name}
                                                    data-cv-block
                                                    data-cv-item-id={skill.id || undefined}
                                                >
                                                    <div className="flex justify-between mb-0.5">
                                                        <span className="text-xs font-bold">{skill.name}</span>
                                                    </div>
                                                    <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full rounded-full"
                                                            style={{ width: `${skill.level}%`, backgroundColor: primaryColor }}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Colonne droite — démarre clairement sous l'en-tête coloré */}
                <div data-cv-main className="flex-1 min-h-0 pt-32 pl-4 overflow-hidden">
                    {hasEducation && (
                        <section className="mb-5">
                            <SectionHeading
                                title={CV_LABELS.education}
                                isContinuation={eduContinued}
                                primaryColor={primaryColor}
                                variant="pill"
                            />
                            <div className="cv-fs-education space-y-3">
                                {data.education.length > 0
                                    ? data.education.map((edu, i) => (
                                          <div
                                              key={edu.id || i}
                                              className="flex gap-4"
                                              data-cv-block
                                              data-cv-item-id={edu.id || undefined}
                                          >
                                              <div className="flex flex-col items-center pt-1">
                                                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: primaryColor }} />
                                                  {i < data.education.length - 1 && (
                                                      <div className="w-0.5 bg-slate-200 mt-1 min-h-[20px] self-stretch" />
                                                  )}
                                              </div>
                                              <div className="flex-1 pb-2">
                                                  <p className="text-xs font-black mb-0.5" style={{ color: primaryColor }}>
                                                      {edu.startDate?.slice?.(0, 4) || edu.startDate} -{' '}
                                                      {edu.isCurrent ? CV_LABELS.present : edu.endDate?.slice?.(0, 4) || edu.endDate}
                                                  </p>
                                                  <h3 className="text-sm font-black mb-0.5">{edu.institution}</h3>
                                                  <p className="text-xs opacity-80">
                                                      {edu.degree}
                                                      {edu.field ? ` — ${edu.field}` : ''}
                                                      {edu.description ? `. ${edu.description}` : ''}
                                                  </p>
                                              </div>
                                          </div>
                                      ))
                                    : showPlaceholders && (
                                          <div className="flex gap-4">
                                              <div className="w-2.5 h-2.5 rounded-full mt-1" style={{ backgroundColor: primaryColor }} />
                                              <div>
                                                  <p className="text-xs font-black mb-0.5" style={{ color: primaryColor }}>2018 - 2022</p>
                                                  <h3 className="text-sm font-black">Établissement</h3>
                                                  <p className="text-xs opacity-80">Diplôme / filière</p>
                                              </div>
                                          </div>
                                      )}
                            </div>
                        </section>
                    )}

                    {hasExperience && (
                        <section className="mb-5">
                            <SectionHeading
                                title={CV_LABELS.experience}
                                isContinuation={expContinued}
                                primaryColor={primaryColor}
                                variant="pill"
                            />
                            <div className="cv-fs-experience space-y-3">
                                {data.experience.length > 0
                                    ? data.experience.map((exp, i) => (
                                          <div
                                              key={exp.id || i}
                                              className="flex gap-4"
                                              data-cv-block
                                              data-cv-item-id={exp.id || undefined}
                                          >
                                              <div className="flex flex-col items-center pt-1">
                                                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: primaryColor }} />
                                                  {i < data.experience.length - 1 && (
                                                      <div className="w-0.5 bg-slate-200 mt-1 min-h-[20px] self-stretch" />
                                                  )}
                                              </div>
                                              <div className="flex-1 pb-2">
                                                  <p className="text-xs font-black mb-0.5" style={{ color: primaryColor }}>
                                                      {exp.startDate} - {frPresent(exp.isCurrent, exp.endDate)}
                                                  </p>
                                                  <h3 className="text-sm font-black mb-0.5">{exp.position}</h3>
                                                  <p className="text-xs opacity-80">
                                                      {exp.company}
                                                      {exp.description ? `. ${exp.description}` : ''}
                                                  </p>
                                              </div>
                                          </div>
                                      ))
                                    : showPlaceholders && (
                                          <div className="flex gap-4">
                                              <div className="w-2.5 h-2.5 rounded-full mt-1" style={{ backgroundColor: primaryColor }} />
                                              <div>
                                                  <p className="text-xs font-black mb-0.5" style={{ color: primaryColor }}>
                                                      2020 - {CV_LABELS.present}
                                                  </p>
                                                  <h3 className="text-sm font-black">Poste</h3>
                                                  <p className="text-xs opacity-80">Entreprise</p>
                                              </div>
                                          </div>
                                      )}
                            </div>
                        </section>
                    )}

                    {hasLanguages && (
                        <section className="mb-2">
                            <SectionHeading
                                title={CV_LABELS.languages}
                                isContinuation={langContinued}
                                primaryColor={primaryColor}
                                variant="pill"
                            />
                            <div className="cv-fs-languages grid grid-cols-3 gap-3">
                                {(data.languages && data.languages.length > 0
                                    ? data.languages.slice(0, 3)
                                    : [
                                          { name: 'Français', level: 100 },
                                          { name: 'Anglais', level: 80 },
                                          { name: 'Espagnol', level: 55 },
                                      ]
                                ).map((lang) => (
                                    <div
                                        key={lang.id || lang.name}
                                        className="flex flex-col items-center"
                                        data-cv-block
                                        data-cv-item-id={lang.id || undefined}
                                    >
                                        <div className="relative w-16 h-16 mb-1.5">
                                            <svg className="transform -rotate-90 w-16 h-16">
                                                <circle cx="32" cy="32" r="26" stroke="#E5E7EB" strokeWidth="5" fill="transparent" />
                                                <circle
                                                    cx="32"
                                                    cy="32"
                                                    r="26"
                                                    stroke={primaryColor}
                                                    strokeWidth="5"
                                                    fill="transparent"
                                                    strokeDasharray={2 * Math.PI * 26}
                                                    strokeDashoffset={2 * Math.PI * 26 * (1 - lang.level / 100)}
                                                    strokeLinecap="round"
                                                />
                                            </svg>
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <span className="text-[10px] font-black" style={{ color: primaryColor }}>
                                                    {Math.round(lang.level)}%
                                                </span>
                                            </div>
                                        </div>
                                        <p className="text-[10px] font-black uppercase text-center">{lang.name}</p>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EarthTonesTemplate;
